import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { IMAGE_URLS } from '../../constants/images';

interface ServicesSectionProps {
  visibleSections: Set<string>;
  expandedService: string | null;
  setExpandedService: (service: string | null) => void;
  servicesSectionRef: React.RefObject<HTMLElement>;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({
  visibleSections,
  expandedService,
  setExpandedService,
  servicesSectionRef,
}) => {
  return (
    <section
      id="services"
      ref={servicesSectionRef}
      className={`relative z-10 py-12 sm:py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-black transition-all duration-1000 ${
        visibleSections.has('services')
          ? 'opacity-100 translate-y-0'
          : 'opacity-0 translate-y-10'
      }`}
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-black/95 to-black pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <h2
          className={`font-megant text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8 text-center transition-all duration-1000 delay-100 ${
            visibleSections.has('services')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          OUR SERVICES
        </h2>
        <p
          className={`font-montserrat text-gray-300 text-base sm:text-lg leading-relaxed text-center mb-8 sm:mb-12 max-w-4xl mx-auto px-4 sm:px-0 transition-all duration-1000 delay-200 ${
            visibleSections.has('services')
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-10'
          }`}
        >
          MIRSAL utilizes a straight-forward yet rigorous approach when executing guest management
          services that meet the most complex needs of events.
        </p>

        {/* Section Divider */}
        <div
          className={`mb-12 h-px bg-gradient-to-r from-transparent via-gray-700 to-transparent transition-all duration-1000 delay-300 ${
            visibleSections.has('services')
              ? 'opacity-100 scale-x-100'
              : 'opacity-0 scale-x-0'
          }`}
        />

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 md:gap-6 min-w-0">
          {/* Guest List Management */}
          <div
            className={`p-4 sm:p-5 md:p-6 transition-all duration-300 min-w-0 break-words rounded-xl hover:shadow-lg hover:shadow-green-400/20 hover:-translate-y-2 hover:scale-[1.02] group relative overflow-hidden flex flex-col ${
              visibleSections.has('services')
                ? 'opacity-100 translate-x-0 scale-100'
                : 'opacity-0 -translate-x-20 scale-95'
            }`}
            style={{
              transitionDelay: visibleSections.has('services') ? '400ms' : '0ms',
              visibility: visibleSections.has('services') ? 'visible' : 'hidden',
            }}
          >
            {/* Animated background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
            
            {/* Icon Section */}
            <div className="mb-4 sm:mb-5 relative z-10 flex justify-center">
              <img 
                src="https://images.squarespace-cdn.com/content/v1/5a5b3f862278e73ef37fb701/9e3fbe5a-6d0e-4b57-8e17-ec8a2edc0df0/GUEST+LIST+MANAGEMENT-04.png?format=500w" 
                alt="Guest List Management" 
                className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain group-hover:scale-110 transition-transform duration-200"
              />
            </div>
            
            {/* Title Section */}
            <div className="mb-4 relative z-10 text-center">
              <h3 className="font-megant text-base md:text-lg font-bold text-white mb-1">
                GUEST LIST MANAGEMENT
              </h3>
              <p className="font-montserrat text-green-400 text-xs md:text-sm underline">
                Collection of Contacts
              </p>
            </div>
            
            {/* Description */}
            <p className="font-montserrat text-gray-300 text-xs md:text-sm mb-4 leading-relaxed relative z-10 text-center line-clamp-2">
              We offer a thorough guest list management service, from checking through your
              identified list to identifying any incomplete addresses, missing details or
              misspelled names, to make sure your invites reach all the selected guests.
            </p>
            <button
              onClick={() =>
                setExpandedService(expandedService === 'guest-list' ? null : 'guest-list')
              }
              className="font-montserrat relative z-20 flex items-center justify-center text-green-400 hover:text-green-300 transition-all duration-300 text-sm md:text-base font-medium cursor-pointer"
            >
              {expandedService === 'guest-list' ? (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Show Less
                  </span>
                  <ChevronUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              ) : (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Learn More
                  </span>
                  <ChevronDown className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              )}
            </button>
            {expandedService === 'guest-list' && (
              <div className="mt-6 pt-6 border-t border-gray-800 space-y-6 relative z-10 overflow-hidden animate-slide-down">
                <div className="animate-fade-in">
                <div>
                  <h4 className="font-megant text-white font-bold mb-2 text-base md:text-lg">
                    Collection of Contacts
                  </h4>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed mb-4">
                    We offer a thorough guest list management service, from checking through your
                    identified list to identifying any incomplete addresses, missing details or
                    misspelled names, to make sure your invites reach all the selected guests. We
                    collect and all relevant contact details, such as phone numbers, email
                    addresses, mailing addresses and party count for all the invitees to a
                    particular event.
                  </p>
                  <h4 className="font-megant text-white font-bold mb-2 text-base md:text-lg">
                    Collection of Contacts
                  </h4>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed mb-4">
                    We offer a thorough guest list management service, from checking through your
                    identified list to identifying any incomplete addresses, missing details or
                    misspelled names, to make sure your invites reach all the selected guests. We
                    collect and all relevant contact details, such as phone numbers, email
                    addresses, mailing addresses and party count for all the invitees to a
                    particular event.
                  </p>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed mb-4">
                    Whether guest names are provided through email, phone or even paper, we will work
                    to ensure all guests are contained within the list. We will then proceed to
                    check each of the names, cross-reference guests' contact information with our
                    records, verify their formal titles and identify head counts. Owing to our large
                    record of contacts and contact details, we will be able to capitalize on its
                    database. Also, we can translate names from English to Arabic as a
                    supplementary charged service.
                  </p>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed">
                    Lastly, we will follow up with any attendees who fail to respond, and further
                    drop everyone a polite reminder as per the client's desire so as to encourage
                    an accurate turnout.
                  </p>
                </div>
                <div>
                  <h4 className="font-megant text-white font-bold mb-2 text-base md:text-lg">
                    Distribution of Invites
                  </h4>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed mb-4">
                    After gathering all guest names and their contact details, we will send out the
                    invitations to all attendees via WhatsApp. This Whatsapp number will be monitored
                    on a regular basis for any RSVPs or inquiries collected.
                  </p>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed">
                    We can also create a personalized email for clients, which will be used for all
                    correspondence related to the event.
                  </p>
                </div>
                <div>
                  <h4 className="font-megant text-white font-bold mb-2 text-base md:text-lg">RSVP Reporting</h4>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed">
                    We provide our clients with a daily RSVP report as well as another RSVP report
                    one week prior to the event detailing all confirmed attendees. This will allow
                    the client to adjust various event arrangements, such as seating, meals, etc.
                  </p>
                </div>
                <div>
                  <h4 className="font-megant text-white font-bold mb-2 text-base md:text-lg">
                    Additional Event Services
                  </h4>
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed mb-2">
                    Should our clients utilize our main guest list management service, we can add on
                    these extra services, including:
                  </p>
                  <ul className="font-montserrat list-disc list-inside text-gray-300 text-sm md:text-base space-y-1 ml-4">
                    <li>Pre-event functions, invitations and RSVPs</li>
                    <li>Post-event functions, invitations and RSVPs</li>
                    <li>
                      Zoning assistance (acting as the middleman between the event planner and
                      hosting)
                    </li>
                    <li>Provision of ushers list</li>
                  </ul>
                </div>
                </div>
              </div>
            )}
          </div>

          {/* Event Website */}
          <div
            className={`p-4 sm:p-5 md:p-6 transition-all duration-300 min-w-0 break-words rounded-xl hover:shadow-lg hover:shadow-green-400/20 hover:-translate-y-2 hover:scale-[1.02] group relative overflow-hidden flex flex-col ${
              visibleSections.has('services')
                ? 'opacity-100 translate-x-0 scale-100'
                : 'opacity-0 translate-x-20 scale-95'
            }`}
            style={{
              transitionDelay: visibleSections.has('services') ? '500ms' : '0ms',
              visibility: visibleSections.has('services') ? 'visible' : 'hidden',
            }}
          >
            {/* Animated background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
            
            {/* Icon Section */}
            <div className="mb-4 sm:mb-5 relative z-10 flex justify-center">
              <img 
                src="https://images.squarespace-cdn.com/content/v1/5a5b3f862278e73ef37fb701/6af96e20-f4f5-48ba-8345-6bdf63f45d8c/Mirsal+Icons-04.png?format=500w" 
                alt="Event Website" 
                className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain group-hover:scale-110 transition-transform duration-200"
              />
            </div>
            
            {/* Title Section */}
            <div className="mb-4 relative z-10 text-center">
              <h3 className="font-megant text-base md:text-lg font-bold text-white mb-1">EVENT WEBSITE</h3>
            </div>
            
            {/* Description */}
            <p className="font-montserrat text-gray-300 text-xs md:text-sm mb-4 leading-relaxed relative z-10 text-center line-clamp-2">
              We have the capacity to develop and manage personalized websites for all types of
              events, including weddings, parties, reunions, corporate events, meetings,
              conferences, banquets, galas, fundraisers, baby showers, birthdays and more.
            </p>
            <button
              onClick={() =>
                setExpandedService(expandedService === 'event-website' ? null : 'event-website')
              }
              className="font-montserrat relative z-20 flex items-center justify-center text-green-400 hover:text-green-300 transition-all duration-300 text-sm md:text-base font-medium cursor-pointer"
            >
              {expandedService === 'event-website' ? (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Show Less
                  </span>
                  <ChevronUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              ) : (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Learn More
                  </span>
                  <ChevronDown className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              )}
            </button>
            {expandedService === 'event-website' && (
              <div className="mt-6 pt-6 border-t border-gray-800 relative z-10 overflow-hidden animate-slide-down">
                <div className="animate-fade-in">
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed">
                    The website will feature the event program, guide for non-residents, and an RSVP
                    and inquiry section that is regularly monitored.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Concierge Services */}
          <div
            className={`p-4 sm:p-5 md:p-6 transition-all duration-300 min-w-0 break-words rounded-xl hover:shadow-lg hover:shadow-green-400/20 hover:-translate-y-2 hover:scale-[1.02] group relative overflow-hidden flex flex-col ${
              visibleSections.has('services')
                ? 'opacity-100 translate-x-0 scale-100'
                : 'opacity-0 -translate-x-20 scale-95'
            }`}
            style={{
              transitionDelay: visibleSections.has('services') ? '600ms' : '0ms',
              visibility: visibleSections.has('services') ? 'visible' : 'hidden',
            }}
          >
            {/* Animated background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
            
            {/* Icon Section */}
            <div className="mb-4 sm:mb-5 relative z-10 flex justify-center">
              <img 
                src={IMAGE_URLS.LANDING_AGENT_ILLUSTRATION} 
                alt="Concierge support" 
                className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain group-hover:scale-110 transition-transform duration-200"
              />
            </div>
            
            {/* Title Section */}
            <div className="mb-4 relative z-10 text-center">
              <h3 className="font-megant text-base md:text-lg font-bold text-white mb-1">CONCIERGE SERVICES</h3>
            </div>
            
            {/* Description */}
            <p className="font-montserrat text-gray-300 text-xs md:text-sm mb-4 leading-relaxed relative z-10 text-center line-clamp-2">
              We offer concierge services to ease and unify the guests' and hosts' experience,
              including hotel bookings, transport arrangement, giveaways, and more.
            </p>
            <button
              onClick={() =>
                setExpandedService(expandedService === 'concierge' ? null : 'concierge')
              }
              className="font-montserrat relative z-20 flex items-center justify-center text-green-400 hover:text-green-300 transition-all duration-300 text-sm md:text-base font-medium cursor-pointer"
            >
              {expandedService === 'concierge' ? (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Show Less
                  </span>
                  <ChevronUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              ) : (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Learn More
                  </span>
                  <ChevronDown className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              )}
            </button>
            {expandedService === 'concierge' && (
              <div className="mt-6 pt-6 border-t border-gray-800 relative z-10 overflow-hidden animate-slide-down">
                <div className="animate-fade-in">
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed mb-4">
                    We offer the below concierge services to ease and unify the guests' and hosts'
                    experience:
                  </p>
                  <ul className="font-montserrat list-disc list-inside text-gray-300 text-sm md:text-base space-y-2 ml-4">
                    <li>Hotel bookings</li>
                    <li>Transport arrangement</li>
                    <li>Giveaways</li>
                    <li>Gift registry</li>
                    <li>
                      Guest packing list, including the right shoes to wear, weather forecast and
                      more
                    </li>
                    <li>Other customizable services upon request</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Event Phone Line */}
          <div
            className={`p-4 sm:p-5 md:p-6 transition-all duration-300 min-w-0 break-words rounded-xl hover:shadow-lg hover:shadow-green-400/20 hover:-translate-y-2 hover:scale-[1.02] group relative overflow-hidden flex flex-col ${
              visibleSections.has('services')
                ? 'opacity-100 translate-x-0 scale-100'
                : 'opacity-0 translate-x-20 scale-95'
            }`}
            style={{
              transitionDelay: visibleSections.has('services') ? '700ms' : '0ms',
              visibility: visibleSections.has('services') ? 'visible' : 'hidden',
            }}
          >
            {/* Animated background gradient on hover */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
            
            {/* Icon Section */}
            <div className="mb-4 sm:mb-5 relative z-10 flex justify-center">
              <img 
                src="https://images.squarespace-cdn.com/content/v1/5a5b3f862278e73ef37fb701/3ae87bcf-0017-4279-9fe6-dd405fbf6f23/phone-04.png?format=500w" 
                alt="Event Phone Line" 
                className="w-40 h-40 sm:w-48 sm:h-48 md:w-56 md:h-56 object-contain group-hover:scale-110 transition-transform duration-200"
              />
            </div>
            
            {/* Title Section */}
            <div className="mb-4 relative z-10 text-center">
              <h3 className="font-megant text-base md:text-lg font-bold text-white mb-1">EVENT PHONE LINE</h3>
            </div>
            
            {/* Description */}
            <p className="font-montserrat text-gray-300 text-xs md:text-sm mb-4 leading-relaxed relative z-10 text-center line-clamp-2">
              Clients can enjoy a dedicated phone line with a personalized automated phone system
              message for their events.
            </p>
            <button
              onClick={() =>
                setExpandedService(expandedService === 'phone-line' ? null : 'phone-line')
              }
              className="font-montserrat relative z-20 flex items-center justify-center text-green-400 hover:text-green-300 transition-all duration-300 text-sm md:text-base font-medium cursor-pointer"
            >
              {expandedService === 'phone-line' ? (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Show Less
                  </span>
                  <ChevronUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              ) : (
                <>
                  <span className="font-montserrat mr-2 group-hover:translate-x-1 transition-transform">
                    Learn More
                  </span>
                  <ChevronDown className="w-4 h-4 group-hover:scale-110 transition-transform" />
                </>
              )}
            </button>
            {expandedService === 'phone-line' && (
              <div className="mt-6 pt-6 border-t border-gray-800 relative z-10 overflow-hidden animate-slide-down">
                <div className="animate-fade-in">
                  <p className="font-montserrat text-gray-300 text-sm md:text-base leading-relaxed">
                    All callers will be greeted by a skilled and supervised contact center agent to
                    collect their RSVPs and answer any of their questions.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;

